'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { ArrowRight, Info, LoaderCircle, Medal } from 'lucide-react'
import { formatPrice, formatImageURL } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { useCart, useLoginForm } from '@/lib/commerce/hooks'

export default function CheckoutLogin({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { cart, initialLoading, isValidating, validateCart, canCheckout, cartTotal, cartCount, orderDiscountAmount, loyaltyDiscountAmount } = useCart()
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - orderDiscountAmount)) : 0
  const total = Math.max(0, cartTotal - (orderDiscountAmount || 0) - loyaltyDiscount)
  const f = useLoginForm({ defaultRedirect: '/checkout' })
  const [hasMounted, setHasMounted] = useState(false)

  const products = cart?.products || []

  useEffect(() => { setHasMounted(true) }, [])

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!hasMounted) return null

  const firstImages = products.slice(0, 3).map((p: any) => {
    const img = p.variant?.images?.[0] || p.product?.images?.[0] || p.image
    return img ? formatImageURL(img) : formatImageURL('placeholder.png')
  })

  return (
    <div className="min-h-170 flex flex-col items-center justify-center px-4 py-16 sm:py-20">
      {initialLoading ? (
        <div
          className="mb-6 inline-flex items-center gap-3 rounded-full px-4 py-2 animate-pulse"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex -space-x-1.5">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="w-6 h-6 rounded-full relative"
                style={{ background: 'rgba(255,255,255,0.08)', border: '2px solid #08080a', zIndex: 3 - i }}
              />
            ))}
          </div>
          <div className="h-3 w-28 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
        </div>
      ) : products.length > 0 ? (
        <div className="mb-6 flex flex-col items-center gap-2">
          <div
            className="inline-flex items-center gap-3 rounded-full px-4 py-2"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="flex -space-x-1.5">
              {firstImages.map((src: string, i: number) => (
                <div
                  key={i}
                  className="w-6 h-6 rounded-full overflow-hidden relative"
                  style={{ border: '2px solid #08080a', zIndex: 3 - i }}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {cartCount} бараа · <span className="text-white font-medium tabular-nums">{formatPrice(total)}</span>
            </span>
          </div>
          {loyaltyDiscount > 0 && (
            <div
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium tabular-nums"
              style={{ background: 'rgba(139,92,246,0.16)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }}
            >
              <Medal className="w-3 h-3 shrink-0" /> Лояалти хөнгөлөлт −{formatPrice(loyaltyDiscount)}
            </div>
          )}
        </div>
      ) : null}

      <div
        className="w-full max-w-105 rounded-2xl p-8"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Захиалга үргэлжлүүлэх</div>
        <h1
          className="mt-2 font-semibold tracking-[-0.02em] leading-[1.05] text-white"
          style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 28 }}
        >
          Тавтай морил
        </h1>

        <form onSubmit={f.handleLogin} className="mt-6 space-y-3">
          <label className="block">
            <div className="text-[12px] font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Утасны дугаар / И-мэйл хаяг
            </div>
            <input
              type="text"
              value={f.emailOrPhone}
              onChange={(e: any) => f.setEmailOrPhone(e.target.value)}
              onKeyDown={f.handleKeyDown}
              autoFocus
              placeholder="mail@shop.mn эсвэл 00000000"
              className="w-full h-11 px-4 rounded-xl text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)', color: 'white' }}
            />
          </label>

          <label className="block">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[12px] font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>Нууц үг</div>
              <Link
                href={navigateUrl('/forgot', pathname)}
                className="text-[11.5px] font-medium text-white transition-opacity hover:opacity-80"
              >
                Нууц үгээ мартсан уу?
              </Link>
            </div>
            <input
              type="password"
              value={f.password}
              onChange={(e: any) => f.setPassword(e.target.value)}
              onKeyDown={f.handleKeyDown}
              placeholder="••••••••"
              className="w-full h-11 px-4 rounded-xl text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)', color: 'white' }}
            />
          </label>

          <button
            type="submit"
            disabled={f.loading}
            className="w-full h-12 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2 cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed mt-1"
            style={{ background: 'white', color: '#0a0a0c' }}
          >
            {f.loading ? <><LoaderCircle className="h-4 w-4 animate-spin" /> Нэвтэрч байна...</> : <>Нэвтрэх <ArrowRight className="h-3.5 w-3.5" /></>}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
          <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.4)' }}>эсвэл</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
        </div>

        <div className="space-y-2">
          <button
            type="button"
            onClick={f.routeToRegister}
            className="w-full h-11 rounded-full text-[13px] font-medium cursor-pointer transition-colors"
            style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            Шинэ бүртгэл үүсгэх
          </button>
          {vendor?.canGuestOrder && (
            <button
              type="button"
              onClick={() => { window.location.href = navigateUrl('/checkout', pathname) }}
              disabled={isValidating || !canCheckout()}
              className="w-full h-11 rounded-full text-[13px] font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'transparent', color: 'rgba(255,255,255,0.6)' }}
            >
              Зочноор үргэлжлүүлэх →
            </button>
          )}
        </div>
      </div>

      {vendor?.canGuestOrder && (
        <div
          className="mt-5 w-full max-w-105 rounded-xl p-3 flex items-start gap-2"
          style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}
        >
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#fcd34d' }} />
          <div className="text-[11.5px] leading-snug" style={{ color: 'rgba(252,211,77,0.85)' }}>
            Зочноор нэвтэрсэн тохиолдолд таны захиалгын түүх хадгалагдахгүй болно.
          </div>
        </div>
      )}
    </div>
  )
}
